import React, { useState, useEffect } from 'react';
import { FileText, Download } from 'lucide-react';
import axios from '../../api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const SubscriptionDiscountReport = () => {
  const [discounts, setDiscounts] = useState([]);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants/subscription-discounts/report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // The axios interceptor auto-unwraps: response.data is now the array, 
      // and pagination metadata is in response.pagination
      const discountData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const paginationMeta = response.pagination || response.data || {};
      setDiscounts(discountData);
      setTotalDiscount(paginationMeta.total_discounts || 0);
    } catch (error) {
      console.error('Error fetching discounts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Bug fix: removed shadowing formatDate – using imported one

  return (
    <div className="p-6" data-testid="subscription-discount-report">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Subscription Discount Report</h1>
          <p className="text-gray-500 mt-1">First-time purchase discounts given to tenants</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total Discounts Given</p>
          <p className="text-2xl font-bold text-red-600" data-testid="total-discount-amount">
            ₹{totalDiscount.toLocaleString('en-IN')}
          </p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total Records</p>
          <p className="text-2xl font-bold text-gray-800" data-testid="total-records">
            {discounts.length}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : discounts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No discount records found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sl</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Original Price</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Final Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {discounts.map((d, index) => (
                <tr key={d.id || index} className="hover:bg-gray-50" data-testid={`discount-row-${index}`}>
                  <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{d.tenant_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{d.plan_name}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-600">₹{(d.original_price || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-sm text-right text-red-600 font-medium">-₹{(d.discount_amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-sm text-right text-green-600 font-bold">₹{(d.final_price || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(d.date)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t">
              <tr>
                <td colSpan="4" className="px-4 py-3 text-sm font-bold text-right text-gray-700">Total Discount:</td>
                <td className="px-4 py-3 text-sm text-right text-red-600 font-bold">-₹{totalDiscount.toLocaleString('en-IN')}</td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
};

export default SubscriptionDiscountReport;
